import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import SocialApi from './api/social';
import AppFooter from './layout/AppFooter';
import AppHeader from './layout/AppHeader';
import Sidebar from './layout/Sidebar';
import AdAccounts from './views/AdAccounts';
import Ads from './views/Ads';
import AdSets from './views/AdSets';
import Campaigns from './views/Campaigns';
import Connections from './views/Connections';
import Home from './views/Home';
import Insights from './views/Insights';
import Login from './views/Login';
import { loginUrl, safeNext } from './utils/next';

// Every page needs a signed-in user: anyone else goes to /login, which comes back to the page afterwards
export default function App() {
  const [session, setSession] = useState(null);
  const location = useLocation();
  const [params] = useSearchParams();

  useEffect(() => {
    SocialApi.session().then(setSession).catch(() => setSession({ is_authenticated: false }));
  }, []);

  if (!session) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (location.pathname === '/login') {
    return session.is_authenticated
      ? <Navigate to={safeNext(params.get('next'))} replace />
      : <Login session={session} onSignedIn={setSession} />;
  }

  if (!session.is_authenticated) {
    return <Navigate to={loginUrl(location.pathname + location.search)} replace />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh' }}>
      <Sidebar session={session} />
      {/* header (wide screens), the page, then the footer */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppHeader session={session} />
        <Box component="main" sx={{ flex: 1, px: { xs: 2, md: 5 }, py: { xs: 2.5, md: 4 } }}>
          <Routes>
            <Route path="/" element={<Home session={session} />} />
            <Route path="/connections" element={<Connections />} />
            <Route path="/ad-accounts" element={<AdAccounts />} />
            <Route path="/campaigns" element={<Campaigns />} />
            <Route path="/adsets" element={<AdSets />} />
            <Route path="/ads" element={<Ads />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
        <AppFooter session={session} />
      </Box>
    </Box>
  );
}
